import React, { useState, useEffect, useContext, useRef } from 'react';
import './DeviceDetails.css';
import convenienceApi from '../../common/api/convenience';
import viewport from '../../common/viewport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import Filter from '../Filter/Filter';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';

const DEVICES_PER_LOAD = 50;

const COLUMNS = [{
    heading: 'Device name',
    sortField: 'Name'
  }, {
    heading: 'Category',
    sortField: 'Category',
  }, {
    heading: 'Device ID',
    sortField: 'ExternalId',
  }, {
    heading: 'Status',
    sortField: null,
  }, {
    heading: 'Individual name',
    sortField: 'MonitoredIndividual.LastName',
  }, {
    heading: 'Location',
    sortField: 'Unit.Name',
  }, {
    heading: 'Facility name',
    sortField: 'Facility.Name',
  }
];

export default function DeviceDetails() {
  const environmentContext = useContext(EnvironmentContext);
  const [trueSearchTerms, setTrueSearchTerms] = useState('');
  const [sortColumn, setSortColumn] = useState('Name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [statusFilters, setStatusFilters] = useState([]);
  const [devices, setDevices] = useState(null);
  const [devicesOnDeck, setDevicesOnDeck] = useState(null);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState(false);
  const [searchTermUpdateTimeout, setSearchTermUpdateTimeout] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [smViewport, setSmViewport] = useState(viewport.isSm());
  const { activeUser: user, hasToken } = environmentContext;
  const mostRecentRequest = useRef(null);
  const inputSearchTermsRef = useRef('');
  const isLoading = useRef(false);

  const setInputSearchTermsRef = (el) => {
    inputSearchTermsRef.current = el;
  }  


  const setMostRecentRequest = (el) => {
    mostRecentRequest.current = el;
  }  

  useEffect(() => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails onMount'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!hasToken) return;

    loadDevices();

    window.scrollTo(0, 0);

    window.addEventListener('resize', checkViewport);
    return function cleanup() {
      window.removeEventListener('resize', checkViewport);
    };
  }, []);

  useEffect(() => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails useEffect [environmentContext.hasToken]'); }
    if (!hasToken) return;

    loadDevices();

  }, [environmentContext.hasToken]);

  useEffect(() => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails useEffect [trueSearchTerms, sortColumn, sortDirection, statusFilters.length  ]'); }
    if (!hasToken) return;

    loadDevices();

  }, [trueSearchTerms, sortColumn, sortDirection, statusFilters.length  ]);

  const checkViewport = () => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails checkViewport'); }
    if (viewport.isSm() !== smViewport) {
      setSmViewport(viewport.isSm());
    }
  }

  const loadDevices = () => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails loadDevices'); }
    if (!hasToken || isLoading.current) return;
    isLoading.current = true;

    let timestamp = Date.now();

    setDevices(null);
    setDevicesOnDeck(null);
    setDevicesLoading(true);
    setDevicesError(false);
    setMostRecentRequest(timestamp);

    const sortOrder = sortColumn ? `${sortColumn} ${sortDirection}` : null;
    let trueSearchTermsTrimmed = trueSearchTerms.trim();
    let commaSeparatedSearchTerms = trueSearchTermsTrimmed.split(' ');
    commaSeparatedSearchTerms = commaSeparatedSearchTerms.map(term => term.trim());
    commaSeparatedSearchTerms = commaSeparatedSearchTerms.join(',');

    convenienceApi.getDeviceDetailsByUser(user, statusFilters, 0, DEVICES_PER_LOAD * 2, sortOrder, commaSeparatedSearchTerms)
      .then(
        result => {
          if (timestamp === mostRecentRequest.current) {
            
            if (result) {
                setDevices(result.filter((device, index) => index < DEVICES_PER_LOAD));
                setDevicesOnDeck(result.filter((device, index) => index >= DEVICES_PER_LOAD));
            }
            setDevicesLoading(false);
            setDevicesError(false);
            setMostRecentRequest(null);
          }
        },
        error => {
          if (timestamp === mostRecentRequest.current) {
            setDevices(null);
            setDevicesOnDeck(null);
            setDevicesLoading(false);
            setDevicesError(true);
            setMostRecentRequest(null);
          }
          })
      isLoading.current = false;
  }

  const loadMoreDevices = () => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails loadMoreDevices'); }
    if (!hasToken) return;

    const moreDevicesToLoad = devicesOnDeck.length >= DEVICES_PER_LOAD;
    const totalDevicesLoaded = devices.length + devicesOnDeck.length;

    const newDevicesToShow = [...devices, ...devicesOnDeck];

    // Add devices on deck to devices
    setDevices(newDevicesToShow);
    setDevicesOnDeck(null);

    // If there are no more devices to load, bail
    if (!moreDevicesToLoad) return;

    const sortOrder = sortColumn ? `${sortColumn} ${sortDirection}` : null;
    let trueSearchTermsTrimmed = trueSearchTerms.trim();
    let commaSeparatedSearchTerms = trueSearchTermsTrimmed.split(' ');
    commaSeparatedSearchTerms = commaSeparatedSearchTerms.map(term => term.trim());
    commaSeparatedSearchTerms = commaSeparatedSearchTerms.join(',');

    // Otherwise fetch more devices
   convenienceApi.getDeviceDetailsByUser(user, statusFilters, totalDevicesLoaded, DEVICES_PER_LOAD, sortOrder, commaSeparatedSearchTerms)
      .then(
        result => {
          setDevicesOnDeck(result);
          
        },
        error => {
          setDevicesOnDeck(null);
        })
  }

  const showSimpleView = () => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails showSimpleView'); }
    if (showDetailView) setShowDetailView(false);
  }

  const showDetailViewThis = () => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails showDetailViewThis'); }
    if (!showDetailView) setShowDetailView(true);
  }

  const getDeviceStatus = (device) => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails getDeviceStatus'); }
    let jsxToRender = []

    if (device.dormant) {
      jsxToRender.push(
        <div key="dormant" className="Device-Details__status-group">
          <FontAwesomeIcon icon="minus-circle" className="Device-Details__dormant-icon" />
          <span className="Device-Details__status-text">Dormant</span>
        </div>);
    }
    if (device.lowBattery) {
      jsxToRender.push(
        <div key="low-battery" className="Device-Details__status-group">
          <FontAwesomeIcon icon="minus-circle" className="Device-Details__low-battery-icon" />
          <span className="Device-Details__status-text">Low Battery</span>
      </div>);
    }
    if (device.missing) {
      jsxToRender.push(
        <div key="missing" className="Device-Details__status-group">
          <FontAwesomeIcon icon="times-circle" className="Device-Details__missing-icon" />
          <span className="Device-Details__status-text">Missing</span>
      </div>);
    }
    if (device.tamper) {
      jsxToRender.push(
        <div key="tampered" className="Device-Details__status-group">
          <FontAwesomeIcon icon="times-circle" className="Device-Details__tampered-icon" />
          <span className="Device-Details__status-text">Tamper</span>
      </div>);
    }

    if (jsxToRender.length === 0) {
      jsxToRender.push(
        <div key="active" className="Device-Details__status-group">
          <FontAwesomeIcon icon="check-circle" className="Device-Details__active-icon" />
          <span className="Device-Details__status-text">Active</span>
      </div>
      );
    }

    return jsxToRender;
  }

  const updateSearchTerms = (e) => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails updateSearchTerms'); }
    if (searchTermUpdateTimeout) clearTimeout(searchTermUpdateTimeout);

    setInputSearchTermsRef(e.target.value);
    setSearchTermUpdateTimeout(setTimeout(updateTrueSearchTerms, 350));
  }

  const updateTrueSearchTerms = () => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails updateTrueSearchTerms'); }
    setTrueSearchTerms(inputSearchTermsRef.current);
    setSearchTermUpdateTimeout(null);
  }  

  const updateStatusFilter = (filter) => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails updateStatusFilter'); }
    setStatusFilters(filter.criteria.map(criterion => criterion.field));
  }  

  const updateSort = (e) => {
    if (logComponent('DeviceDetails')) { console.debug('DeviceDetails updateSort'); }
    const target = e.target;

    let newDirection = 'asc'; 

    if (sortColumn === target.dataset.column) {
        newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortColumn(target.dataset.column);
    setSortDirection(newDirection);
  }

  return (
    <div className="Device-Details">
      <div className="Device-Details__main">
        <p className="Device-Details__heading">Device Details</p>
        <ul className="Device-Details__mobile-tabs">
          <li key="simple" className="Device-Details__mobile-tab">
            <button 
              className={'Device-Details__mobile-tab-button' + (!showDetailView ? ' Device-Details__mobile-tab-button--selected' : '') }
              onClick={showSimpleView}>Simple View</button>
          </li>
          <li key="detail" className="Device-Details__mobile-tab">
            <button
              className={'Device-Details__mobile-tab-button' + (showDetailView ? ' Device-Details__mobile-tab-button--selected' : '') }
              onClick={showDetailViewThis}>Detail View</button>
          </li>            
        </ul>
        <div className="Device-Details__filter-row">
        <div className="Device-Details__search-input-wrapper">
            <FontAwesomeIcon icon="search" className="Device-Details__search-icon" />
            <input 
              type="text" 
              className="Device-Details__search-input"
              placeholder="Search devices"
              value={inputSearchTermsRef.current}
              onChange={updateSearchTerms}/>
          </div>        

          <div className="Device-Details__filter-wrapper">
            <Filter
              filters={[
                {
                  name: 'status',
                  options: [{
                    text: 'Dormant',
                    criteria: [{
                      field: 'Dormant',
                      acceptedValues: [true]
                    }]
                  }, {
                    text: 'Low Battery',
                    criteria: [{
                      field: 'LowBattery',
                      acceptedValues: [true]
                    }]
                  }, {
                    text: 'Missing',
                    criteria: [{
                      field: 'Missing',
                      acceptedValues: [true]
                    }]
                  }, {
                    text: 'Tamper',
                    criteria: [{
                      field: 'Tamper',
                      acceptedValues: [true]
                    }]
                  }],
                  title: 'Status',
                  type: 'checkbox'
                }
              ]} 
              onChange={updateStatusFilter} />
          </div>
      </div>
        {
          !smViewport ?
          <div className="Device-Details__device-list">
            <table className="Device-Details__large-table">
              <thead>
                <tr>
                {
                  COLUMNS.map(column =>
                    user.groupTypeString !== 'Facility' || column.heading !== 'Facility name' ?
                    <th key={column.heading}>
                    {
                      column.sortField ?
                      <button 
                        className={'Device-Details__column-heading' + (sortColumn === column.sortField ? ' Device-Details__column-heading--selected': '') }
                        onClick={updateSort}
                        data-column={column.sortField}>
                        { column.heading }
                        {
                          sortColumn === column.sortField &&
                          <FontAwesomeIcon 
                            icon={( sortDirection === 'asc' ? 'arrow-up' : 'arrow-down')}
                            className="Notes-History__sort-icon" /> 
                        }
                      </button> :
                      <p className="Device-Details__column-heading">{ column.heading }</p>
                    }
                    </th> :
                    null
                  )
                }                                                                         
                </tr>
              </thead>
              <tbody>
              {
                devices && 
                devices.length > 0 &&
                devices
                  .map(device => 
                  <tr 
                    key={device.alarmDeviceId}>
                    <td className="Device-Details__device-name-cell">{ device.name }</td>
                    <td className="Device-Details__category-cell">{ device.category }</td>
                    <td className="Device-Details__id-cell">{ device.externalId }</td>
                    <td className="Device-Details__status-cell">{ getDeviceStatus(device) }</td>
                      <td className="Device-Details__monitoredindividual-name-cell">{ device.monitoredIndividualId ? `${device.monitoredIndividualLegalFirstName} ${device.monitoredIndividualLastName}` : ' ' }</td>
                    <td className="Device-Details__location-cell">{ device.unitId ? device.unitName : ' ' }</td>
                    {
                      user.groupTypeString !== 'Facility' &&
                      <td className="Device-Details__facility-name-cell">{ device.facilityId ? device.facilityName : ' ' }</td>
                    }
                  </tr>
                ) 
              }           
              </tbody>
            </table>
            {
              devices && devices.length === 0 &&
              <p className="Device-Details__empty-message">No devices found...</p>
            }
          </div> :
          <div className="Device-Details__device-list--mobile">
          {
            showDetailView ?
            <div className="Device-Details__device-list-details">
            {
              devices &&
              <ul className="Device-Details__stacked-detail-list">
              {
                devices
                  .map(device => 
                    <li 
                      className="Device-Details__stacked-detail-item"
                      key={device.alarmDeviceId}
                      >
                      <dl>
                        <dt>Device name</dt>
                        <dd>{ device.name }</dd>
                        <dt>Category</dt>
                        <dd>{ device.category }</dd>
                        <dt>Device ID</dt>
                        <dd>{ device.externalId }</dd>
                        <dt>Status</dt>
                        <dd>{ getDeviceStatus(device) }</dd>
                        <dt>Individual name</dt>
                        <dd>{ device.monitoredIndividualId ? `${device.monitoredIndividualLegalFirstName} ${device.monitoredIndividualLastName}` : 'N/A' }</dd>
                        <dt>Location</dt>
                        <dd>{ device.unitId ? device.unitName : 'N/A' }</dd>
                        {
                          user.groupTypeString !== 'Facility' && 
                          <React.Fragment>
                          <dt>Facility name</dt>
                          <dd>{ device.facilityId ? device.facilityName : ' ' }</dd>
                          </React.Fragment>
                        }
                      </dl>
                    </li>
                  )
              }
              </ul>
            }
              <ul> </ul>
            </div> :
            <div className="Device-Details__device-list-simple">
              <table className="Device-Details__small-table">
                <thead>
                  <tr>
                    <th>
                      <button 
                        className={'Device-Details__column-heading' + (sortColumn === 'ExternalId' ? ' Device-Details__column-heading--selected': '') }
                        onClick={updateSort}
                        data-column="ExternalId">
                        Device Id
                        {
                          sortColumn === 'ExternalId' &&
                          <FontAwesomeIcon 
                            icon={( sortDirection === 'asc' ? 'arrow-up' : 'arrow-down')}
                            className="Device-Details__sort-icon" /> 
                        }
                      </button>
                    </th>
                    <th>
                      <button 
                        className={'Device-Details__column-heading' + (sortColumn === 'MonitoredIndividual.LastName' ? ' Device-Details__column-heading--selected': '') }
                        onClick={updateSort}
                        data-column="MonitoredIndividual.LastName">
                        Individual name
                        {
                          sortColumn === 'MonitoredIndividual.LastName' &&
                          <FontAwesomeIcon 
                            icon={( sortDirection === 'asc' ? 'arrow-up' : 'arrow-down')}
                            className="Device-Details__sort-icon" /> 
                        }
                      </button>                        
                    </th>
                    <th>
                      <p className="Device-Details__column-heading">Status</p>
                    </th>
                  </tr>
                </thead>
                <tbody>
                {
                  devices && 
                  devices
                    .map(device => 
                    <tr key={device.alarmDeviceId} >
                      <td className="Device-Details__id-cell">{ device.externalId }</td>
                        <td className="Device-Details__monitoredindividual-name-cell">{ device.monitoredIndividualId ? `${device.monitoredIndividualLegalFirstName} ${device.monitoredIndividualLastName}` : '' }</td>
                      <td className="Device-Details__status-cell">{ getDeviceStatus(device) }</td>
                    </tr>
                  )
                }           
                </tbody>
              </table>

            </div>
          }
          </div>
        }

        <ModuleLoadingStatus margin="3rem" isLoading={devicesLoading} isError={devicesError} reloadAction={loadDevices} />
        {
          devicesOnDeck &&
          devicesOnDeck.length > 0 &&
          <button 
            className="Device-Details__load-more-button"
            onClick={loadMoreDevices}>Load more</button>
        }
      </div>
    </div>
  );
}